import type { AxiosRequestConfig } from 'axios';
import { fetcher, post, put, patch, postWithFiles, del, api } from '../../../services/api';
import { decodeTaskNotes, encodeTaskNotes } from '../utils/notesEncoding';
import type { ReminderPayload } from '../types/tasks';
import type {
  TaskViewType,
  TrackedActionsViewType,
  RecommendedViewType,
  ApplicationTaskResponse,
  ApplicationTaskListResponse,
  BucketGroupResponse,
  RecommendedBucketGroupResponse,
  CreateTaskPayload,
  UpdateTaskPayload,
  CreateRecommendedTaskPayload,
  AttachmentResponse,
  ScheduleTaskFocusTimePayload,
  FocusTimeInitResponse,
  ScheduleFocusTimeResponse,
  DeleteFocusTimeResponse,
  CheckConflictPayload,
  CheckConflictResponse,
  GetInviteesResponse,
  GetLocationsResponse,
  FromContentResponse,
  ApproveFromContentPayload,
  ApproveFromContentResponse,
  ApproveRecommendedTaskResponse,
  BulkCreatePayload,
  CategoryApiResponse,
  GetCategoriesApiResponse,
  CreateCategoryPayload,
  UpdateCategoryPayload,
  UpdateCategoryApiResponse,
  DeleteCategoryApiResponse,
} from '../types/tasksApi';

export type {
  TaskViewType,
  TrackedActionsViewType,
  RecommendedViewType,
  ApplicationTaskResponse,
  ApplicationTaskListResponse,
  BucketGroupResponse,
  RecommendedBucketGroupResponse,
  CreateTaskPayload,
  UpdateTaskPayload,
  CreateRecommendedTaskPayload,
  AttachmentResponse,
  ScheduleTaskFocusTimePayload,
  UpdateFocusTimePayload,
  FocusTimeInitResponse,
  FocusTimeSuggestedSlot,
  ScheduleFocusTimeResponse,
  DeleteFocusTimeResponse,
  CheckConflictPayload,
  CheckConflictResponse,
  GetInviteesResponse,
  GetLocationsResponse,
  ExtractedTask,
  ExtractedPerson,
  FromContentResponse,
  ApproveFromContentPayload,
  ApproveFromContentResponse,
  BulkCreatePayload,
  CategoryApiResponse,
  CreateCategoryPayload,
} from '../types/tasksApi';

export const getMyTasks = (view: TaskViewType, signal?: AbortSignal): Promise<BucketGroupResponse[]> =>
  fetcher<BucketGroupResponse[]>(`/api/v1/to-do/my-tasks?view=${view}&include_focus_time=true`, { signal });

export const getRecommendedTasks = (view: RecommendedViewType = 'all', signal?: AbortSignal): Promise<RecommendedBucketGroupResponse[]> =>
  fetcher<RecommendedBucketGroupResponse[]>(`/api/v1/to-do/recommended/tasks?view=${view}`, { signal });

export const getTrackedActionTasks = (view: TrackedActionsViewType, signal?: AbortSignal): Promise<BucketGroupResponse[]> =>
  fetcher<BucketGroupResponse[]>(`/api/v1/to-do/tracked-actions/tasks?view=${view}`, { signal });

export const createTask = async (payload: CreateTaskPayload): Promise<ApplicationTaskResponse> => {
  const { files = [], ...data } = payload;
  const body = 'notes' in data ? { ...data, notes: encodeTaskNotes(data.notes) } : data;
  const created = await postWithFiles<ApplicationTaskResponse>('/api/v1/to-do/create', body, files);
  return { ...created, notes: decodeTaskNotes(created.notes) ?? null };
};

export const deleteTask = (taskId: string): Promise<void> =>
  del<void>(`/api/v1/to-do/delete/${taskId}`);

export const clearCompletedTasks = (): Promise<{ deleted_count: number; list: string }> =>
  del<{ deleted_count: number; list: string }>('/api/v1/to-do/tasks/completed', { params: { list_type: 'My Tasks' } });

export const clearCompletedTrackedActionTasks = (): Promise<{ deleted_count: number; list: string }> =>
  del<{ deleted_count: number; list: string }>('/api/v1/to-do/tasks/completed', { params: { list_type: 'Tracked actions' } });

export const clearRecommendedTasks = (): Promise<void> =>
  del<void>('/api/v1/to-do/recommended/delete-all');

export const acceptRecommendedTask = (taskId: string): Promise<ApproveRecommendedTaskResponse> =>
  post<ApproveRecommendedTaskResponse>(`/api/v1/to-do/ai-agent/approve`, { recommended_task_ids: [taskId] });

export const sendReminder = (payload: ReminderPayload): Promise<{ failed_recipients?: string[] } | void> =>
  post<{ failed_recipients?: string[] } | void>(`/api/v1/to-do/tasks/${payload.taskId}/send-reminder`, {
    subject: payload.subject,
    body: payload.body,
    recipients: payload.recipients,
  });

const mapProfile = (profile: { teams_agent: boolean | null; mail_agent: boolean | null }) => ({
  teams: profile.teams_agent ?? true,
  outlook: profile.mail_agent ?? true,
});

export const getChannelPreferences = (): Promise<{ teams: boolean; outlook: boolean }> =>
  fetcher<{ teams_agent: boolean | null; mail_agent: boolean | null }>('/api/v1/to-do/user-profile')
    .then(mapProfile)
    .catch(async (err) => {
      if (err?.response?.status === 404) {
        const profile = await post<{ teams_agent: boolean | null; mail_agent: boolean | null }>(
          '/api/v1/to-do/user-profile',
          null
        );
        return mapProfile(profile);
      }
      throw err;
    });

export const saveChannelPreferences = (preferences: { teams: boolean; outlook: boolean }): Promise<void> =>
  patch<void>('/api/v1/to-do/user-profile/agents', {
    teams_agent: preferences.teams,
    mail_agent: preferences.outlook,
  });

export const updateTask = (taskId: string, payload: UpdateTaskPayload): Promise<void> =>
  patch<void>(
    `/api/v1/to-do/update/${taskId}`,
    'notes' in payload ? { ...payload, notes: encodeTaskNotes(payload.notes) } : payload,
  );

export const updateTaskBucket = (taskId: string, bucket: string): Promise<void> =>
  patch<void>(`/api/v1/to-do/update/${taskId}`, { bucket });

export const updateTaskStatus = (taskId: string, status: 'completed' | 'notStarted'): Promise<void> =>
  patch<void>(`/api/v1/to-do/update/${taskId}`, { status });

export const updateTaskType = (taskId: string, type: 'Tracked actions' | 'My Tasks', bucket?: string): Promise<void> =>
  patch<void>(`/api/v1/to-do/update/${taskId}`, { type, display_order: -1, ...(bucket ? { bucket } : {}) });

export const createTaskList = (displayName: string): Promise<ApplicationTaskListResponse> =>
  post<ApplicationTaskListResponse>('/api/v1/to-do/lists', { display_name: displayName });

export const renameTaskList = (taskListId: string, displayName: string): Promise<ApplicationTaskListResponse> =>
  patch<ApplicationTaskListResponse>(`/api/v1/to-do/lists/${taskListId}`, { display_name: displayName });

export const deleteTaskList = (taskListId: string): Promise<void> =>
  del<void>(`/api/v1/to-do/lists/${taskListId}`);

export const downloadAttachment = async (taskListId: string, taskId: string, attachmentId: string): Promise<Blob> => {
  const { data } = await api.get(`/api/v1/to-do/tasks/${taskListId}/${taskId}/attachments/${attachmentId}/download`, {
    responseType: 'blob',
  });
  const blob = data as Blob;

  // The backend returns base64-encoded content (potentially double-encoded due to
  // the Kiota SDK serializer encoding already-encoded bytes). Iteratively decode
  // until we get actual binary file content.
  try {
    let content: string = await blob.text();
    const base64Pattern = /^[A-Za-z0-9+/\-_\r\n]+=*$/;
    let iterations = 0;
    const maxIterations = 3;

    while (iterations < maxIterations) {
      const trimmed = content.trim();
      if (!trimmed || !base64Pattern.test(trimmed)) break;

      // Normalise URL-safe base64 to standard
      const standardBase64 = trimmed.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(standardBase64);

      // Check if we reached actual binary (non-ASCII-printable bytes)
      const hasBinary = decoded.split('').some((ch) => {
        const code = ch.charCodeAt(0);
        return code > 127 || (code < 32 && code !== 10 && code !== 13);
      });

      if (hasBinary) {
        // This is the final decoded binary content
        const bytes = new Uint8Array(decoded.length);
        for (let i = 0; i < decoded.length; i++) {
          bytes[i] = decoded.charCodeAt(i);
        }
        return new Blob([bytes], { type: blob.type });
      }

      // Still looks like text (possibly another layer of base64)
      content = decoded;
      iterations++;
    }
  } catch {
    // Decoding failed, return original blob
  }
  return blob;
};

export const getAttachmentViewUrl = (taskListId: string, taskId: string, attachmentId: string): string =>
  `/api/v1/to-do/tasks/${encodeURIComponent(taskListId)}/${encodeURIComponent(taskId)}/attachments/${encodeURIComponent(attachmentId)}/view`;

export const addAttachment = (taskId: string, file: File): Promise<AttachmentResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  return api
    .post<AttachmentResponse>(`/api/v1/to-do/tasks/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(({ data }) => data);
};

export const deleteAttachment = (taskId: string, attachmentId: string): Promise<void> =>
  del<void>(`/api/v1/to-do/tasks/${taskId}/attachments/${attachmentId}`);

export const createRecommendedTask = (payload: CreateRecommendedTaskPayload): Promise<ApplicationTaskResponse> => {
  const { files = [], ...data } = payload;
  const body = 'notes' in data ? { ...data, notes: encodeTaskNotes(data.notes) } : data;
  return postWithFiles<ApplicationTaskResponse>('/api/v1/to-do/recommended/create', body, files);
};

export const getTaskFocusTime = (taskId: string): Promise<FocusTimeInitResponse> =>
  fetcher<FocusTimeInitResponse>(`/api/v1/to-do/tasks/${taskId}/focus-time`);

export const scheduleTaskFocusTime = (taskId: string, payload: ScheduleTaskFocusTimePayload): Promise<ScheduleFocusTimeResponse> =>
  post<ScheduleFocusTimeResponse>(`/api/v1/to-do/tasks/${taskId}/focus-time`, payload);

export const updateTaskFocusTime = (taskId: string, eventId: string, payload: Partial<ScheduleTaskFocusTimePayload>): Promise<ScheduleFocusTimeResponse> =>
  put<ScheduleFocusTimeResponse>(`/api/v1/to-do/tasks/${taskId}/focus-time/${eventId}`, payload);

export const deleteTaskFocusTime = (taskId: string, eventId: string): Promise<DeleteFocusTimeResponse> =>
  del<DeleteFocusTimeResponse>(`/api/v1/to-do/tasks/${taskId}/focus-time/${eventId}`);

export const checkFocusTimeConflict = (taskId: string, payload: CheckConflictPayload): Promise<CheckConflictResponse> =>
  post<CheckConflictResponse>(`/api/v1/to-do/tasks/${taskId}/focus-time/check-conflict`, payload);

export const getFocusTimeInvitees = (params?: {
  q?: string;
  organiser_email?: string;
  include_all_contacts?: boolean;
  top?: number;
  skip?: number;
}): Promise<GetInviteesResponse> => {
  const query = new URLSearchParams();
  if (params?.q) query.set('q', params.q);
  if (params?.organiser_email) query.set('organiser_email', params.organiser_email);
  if (params?.include_all_contacts !== undefined) query.set('include_all_contacts', String(params.include_all_contacts));
  if (params?.top !== undefined) query.set('top', String(params.top));
  if (params?.skip !== undefined) query.set('skip', String(params.skip));
  const qs = query.toString();
  const url = qs ? `/api/v1/to-do/focus-time/invitees?${qs}` : '/api/v1/to-do/focus-time/invitees';
  return fetcher<GetInviteesResponse>(url);
};

export const getFocusTimeLocations = (): Promise<GetLocationsResponse> =>
  fetcher<GetLocationsResponse>('/api/v1/to-do/focus-time/locations');

export const moveTaskBucket = (taskId: string, bucket: string): Promise<void> =>
  patch<void>(`/api/v1/to-do/update/${taskId}`, { bucket });

export const completeTask = (taskId: string, isCompleted: boolean, completedAt?: string): Promise<void> =>
  patch<void>(`/api/v1/to-do/update/${taskId}`, {
    status: isCompleted ? 'completed' : 'notStarted',
    completed_date_time: isCompleted && completedAt ? completedAt : null,
  });

export const reorderTasks = (payload: { taskId: string; new_order: number }): Promise<void> =>
  patch<void>(`/api/v1/to-do/update/${payload.taskId}`, { display_order: payload.new_order });

export const convertTrackedActionToMyTask = (taskId: string, bucket: string): Promise<void> =>
  patch<void>(`/api/v1/to-do/update/${taskId}`, { type: 'My Tasks', bucket, display_order: -1 });

export const markTrackedActionsViewed = (): Promise<void> =>
  post<void>('/api/v1/to-do/tracked-actions/mark-viewed', {});

export const markRecommendedViewed = (taskIds: string[]): Promise<void> =>
  post<void>('/api/v1/to-do/recommended/mark-viewed', { task_ids: taskIds });

export const runAiAgent = (): Promise<{ thread_id: string; run_id: string }> =>
  post<{ thread_id: string; run_id: string }>('/api/v1/to-do/ai-agent/run', {});

export const getAiAgentStatus = (threadId: string, runId: string): Promise<{ status: string }> =>
  fetcher<{ status: string }>(`/api/v1/to-do/ai-agent/status?thread_id=${encodeURIComponent(threadId)}&run_id=${encodeURIComponent(runId)}`);

export const getTaskReminderPreview = (taskId: string): Promise<{ subject: string; body: string }> =>
  fetcher<{ subject: string; body: string }>(`/api/v1/to-do/tasks/${taskId}/create-reminder`);

export const getTaskReminders = (taskId: string): Promise<{ reminders: { name: string; email: string; sent_at: string }[] }> =>
  fetcher<{ reminders: { name: string; email: string; sent_at: string }[] }>(`/api/v1/to-do/tasks/${taskId}/reminders`);

export const extractTasksFromContent = (text: string, files: File[], config?: AxiosRequestConfig): Promise<FromContentResponse> =>
  postWithFiles<FromContentResponse>('/api/v1/to-do/ai-agent/from-content', { text }, files, config);

export const approveFromContentTasks = (payload: ApproveFromContentPayload, files: File[]): Promise<ApproveFromContentResponse> =>
  postWithFiles<ApproveFromContentResponse>(
    '/api/v1/to-do/ai-agent/from-content/approve',
    { ...payload, tasks: payload.tasks.map((task) => ({ ...task, notes: encodeTaskNotes(task.notes) })) },
    files,
  );

export const bulkCreateTasks = (payload: BulkCreatePayload): Promise<void> =>
  post<void>('/api/v1/to-do/bulk-create', {
    ...payload,
    tasks: payload.tasks.map((task) => ({ ...task, notes: encodeTaskNotes(task.notes) })),
  });

// ── Category API ─────────────────────────────────────────────────

export const getCategories = (): Promise<CategoryApiResponse[]> =>
  fetcher<GetCategoriesApiResponse>('/api/v1/to-do/focus-time/categories')
    .then((res) => res.categories);

export const createCategory = (payload: CreateCategoryPayload): Promise<CategoryApiResponse> =>
  post<CategoryApiResponse>('/api/v1/to-do/focus-time/categories', payload);

export const updateCategory = (categoryName: string, payload: UpdateCategoryPayload): Promise<UpdateCategoryApiResponse> =>
  put<UpdateCategoryApiResponse>(`/api/v1/to-do/focus-time/categories/${encodeURIComponent(categoryName)}`, payload);

export const deleteCategory = (categoryName: string): Promise<DeleteCategoryApiResponse> =>
  del<DeleteCategoryApiResponse>(`/api/v1/to-do/focus-time/categories/${encodeURIComponent(categoryName)}`);
